// @ts-check

import { atom, useAtomValue } from "jotai"
import { store } from "@/lib/store"

/**
 * @typedef {{ id?: string | null, old_string?: string | null, new_string?: string | null, rationale?: string | null }} DraftedEditData
 * @typedef {{ number: number, edit: DraftedEditData }} DraftedEdit
 * @typedef {{
 *   stage: string,
 *   section: string | null,
 *   queries: string[],
 *   startedAt: number,
 *   drafted: DraftedEdit[],
 *   saved: { added: number, skipped: number, failed: boolean } | null,
 * }} ReviewProgress
 */

/**
 * Live progress of a canvas review, per chat session. It comes only from the
 * stream (ReviewProgress and EditDrafted events) and is not saved. Sending a
 * message clears it, because the old progress must not show for the new run.
 */
const reviewProgressAtom = atom(/** @type {Record<string, ReviewProgress>} */({}))

/**
 * @param {number} [elapsedSeconds] time the run has already taken
 * @returns {ReviewProgress}
 */
function newReviewProgress(elapsedSeconds = 0) {
  return {
    stage: "reading",
    section: null,
    queries: [],
    startedAt: Date.now() - elapsedSeconds * 1000,
    drafted: [],
    saved: null,
  }
}

/** @param {string} sessionId */
function clearReviewProgress(sessionId) {
  store.set(reviewProgressAtom, (all) => {
    if (!(sessionId in all)) return all
    const { [sessionId]: _removed, ...rest } = all
    return rest
  })
}

/**
 * Shows a review as started at once. Its first event takes a few seconds.
 * @param {string} sessionId
 */
function startReviewProgress(sessionId) {
  store.set(reviewProgressAtom, (all) => ({ ...all, [sessionId]: newReviewProgress() }))
}

/**
 * @param {string} sessionId
 * @param {{ event: "ReviewProgress" | "EditDrafted", progress: Record<string, any> }} event
 */
function applyReviewProgressEvent(sessionId, event) {
  const { progress } = event
  store.set(reviewProgressAtom, (all) => {
    // elapsed_s is measured on the server; keep the client timer in step with it.
    const current = all[sessionId] ?? newReviewProgress(progress.elapsed_s ?? 0)

    if (event.event === "EditDrafted") {
      return {
        ...all,
        [sessionId]: { ...current, drafted: [...current.drafted, { number: progress.number, edit: progress.edit ?? {} }] },
      }
    }

    const stage = progress.stage ?? current.stage
    return {
      ...all,
      [sessionId]: {
        ...current,
        stage,
        // A section belongs to the "thinking" stage only.
        section: stage === "thinking" ? (progress.section ?? current.section) : null,
        // Searches in one model turn run at the same time, each with its own event.
        queries: stage === "searching"
          ? [...(current.stage === "searching" ? current.queries : []), progress.query].filter(Boolean)
          : [],
        saved: stage === "edits_saved"
          ? { added: progress.added ?? 0, skipped: progress.skipped ?? 0, failed: Boolean(progress.failed) }
          : current.saved,
      },
    }
  })
}

/** @param {string | null | undefined} sessionId */
function useReviewProgress(sessionId) {
  const all = useAtomValue(reviewProgressAtom)
  return sessionId ? (all[sessionId] ?? null) : null
}

export { reviewProgressAtom, applyReviewProgressEvent, clearReviewProgress, startReviewProgress, useReviewProgress }
